import React from 'react';
import type { PeriodStats, ReportDetailLabels } from '../../../shared/types/reports';

export interface MoodComparisonProps {
  currentPeriodStats: PeriodStats;
  previousPeriodStats: PeriodStats | null;
  periodType: 'week' | 'month';
  labels: ReportDetailLabels;
}

export function MoodComparison({
  currentPeriodStats,
  previousPeriodStats,
  periodType,
  labels,
}: MoodComparisonProps) {
  const periodName = periodType === 'week' ? 'Week' : 'Month';

  const formatPeriod = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  };

  const moodChange = currentPeriodStats.averageMood !== null && previousPeriodStats?.averageMood !== null
    ? currentPeriodStats.averageMood - previousPeriodStats.averageMood
    : null;

  return (
    <div className="row g-3">
      {/* Previous Period */}
      <div className="col-12 col-md-6">
        <div className="text-center p-3 bg-light rounded">
          <h6 className="text-muted mb-1">{labels.previousPeriod} {periodName}</h6>
          {previousPeriodStats && (
            <p className="text-muted small mb-2">
              {formatPeriod(previousPeriodStats.periodStart, previousPeriodStats.periodEnd)}
            </p>
          )}
          {previousPeriodStats?.averageMood !== null ? (
            <div className="display-5 fw-bold text-primary">
              {previousPeriodStats.averageMood.toFixed(1)}
            </div>
          ) : (
            <div className="text-muted">No data</div>
          )}
        </div>
      </div>

      {/* Current Period */}
      <div className="col-12 col-md-6">
        <div className="text-center p-3 bg-light rounded">
          <h6 className="text-muted mb-1">{labels.currentPeriod} {periodName}</h6>
          <p className="text-muted small mb-2">
            {formatPeriod(currentPeriodStats.periodStart, currentPeriodStats.periodEnd)}
          </p>
          {currentPeriodStats.averageMood !== null ? (
            <>
              <div className="display-5 fw-bold text-success">
                {currentPeriodStats.averageMood.toFixed(1)}
              </div>
              {moodChange !== null && (
                <div className="mt-2">
                  {moodChange > 0 ? (
                    <span className="badge bg-success">
                      <i className="bi bi-arrow-up me-1"></i>
                      {moodChange.toFixed(1)}
                    </span>
                  ) : moodChange < 0 ? (
                    <span className="badge bg-danger">
                      <i className="bi bi-arrow-down me-1"></i>
                      {Math.abs(moodChange).toFixed(1)}
                    </span>
                  ) : (
                    <span className="badge bg-secondary">No change</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-muted">No data</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MoodComparison;
